import { api } from './authService';

// SAP字段接口
export interface SapField {
  FieldID: number;
  AliasID: string;
  Descr: string;
  FieldType: string;
  SizeID: number;
  NotNull: 'Y' | 'N';
  [key: string]: unknown;
}

// SAP配置接口
export interface SapConfig {
  company: string;
  tableId: string;
  lang: string;
  fieldType?: 'document' | 'table' | 'business_object';
}

// 获取SAP字段请求接口
export interface GetSapFieldsRequest {
  serverId?: number; // 可选，后台会自动获取服务配置
  company: string;
  tableId?: string; // 可选，默认为ODRF
  lang: string;
  fieldType?: 'document' | 'table' | 'business_object';
}

// 获取SAP字段响应接口
export interface GetSapFieldsResponse {
  success: boolean;
  data: {
    fields: SapField[];
    count: number;
    server: {
      id: number;
      name: string;
      url: string;
    };
    config: SapConfig;
  };
}

// 类型映射响应接口
export interface TypeMappingResponse {
  success: boolean;
  data: {
    mapping: Record<string, string>;
    description: string;
  };
}

// 验证配置响应接口
export interface ValidateConfigResponse {
  success: boolean;
  data: {
    isValid: boolean;
    errors: string[];
    config: SapConfig;
  };
}

class SAPB1ServiceClass {
  /**
   * 获取SAP字段信息
   */
  async getSapFields(tenant: string, request: GetSapFieldsRequest): Promise<GetSapFieldsResponse> {
    try {
      const response = await api.post(`/${tenant}/sapb1/sap-fields`, request);
      return response.data;
    } catch (error: unknown) {
      console.error('获取SAP字段失败:', error);
      throw new Error((error as any).response?.data?.message || '获取SAP字段失败');
    }
  }

  /**
   * 获取可用的公司代码列表
   */
  async getAvailableCompanies(tenant: string): Promise<{ success: boolean; data: { companies: string[]; count: number } }> {
    try {
      const response = await api.get(`/${tenant}/sapb1/companies`);
      return response.data;
    } catch (error: unknown) {
      console.error('获取公司代码列表失败:', error);
      throw new Error((error as any).response?.data?.message || '获取公司代码列表失败');
    }
  }

  /**
   * 获取SAP字段类型映射
   */
  async getSapTypeMapping(tenant: string): Promise<TypeMappingResponse> {
    try {
      const response = await api.get(`/${tenant}/sapb1/type-mapping`);
      return response.data;
    } catch (error: unknown) {
      console.error('获取类型映射失败:', error);
      throw new Error((error as any).response?.data?.message || '获取类型映射失败');
    }
  }

  /**
   * 验证SAP配置
   */
  async validateSapConfig(tenant: string, config: SapConfig): Promise<ValidateConfigResponse> {
    try {
      const response = await api.post(`/${tenant}/sapb1/validate-config`, config);
      return response.data;
    } catch (error: unknown) {
      console.error('验证SAP配置失败:', error);
      throw new Error((error as any).response?.data?.message || '验证SAP配置失败');
    }
  }

  /**
   * 将SAP字段类型映射到JSON Schema类型
   */
  mapSapTypeToSchemaType(sapType: string): string {
    const typeMapping: Record<string, string> = {
      'String': 'string',
      'Text': 'string',
      'Number': 'number',
      'Integer': 'number',
      'Decimal': 'number',
      'Amount': 'number',
      'Date': 'date',
      'DateTime': 'date',
      'Boolean': 'boolean',
      'Enum': 'string'
    };
    
    return typeMapping[sapType] || 'string';
  }

  /**
   * 格式化SAP字段用于显示
   */
  formatSapFieldForDisplay(field: SapField): {
    id: number;
    name: string;
    description: string;
    type: string;
    schemaType: string;
    size: number;
    required: boolean;
    displayText: string;
  } {
    return {
      id: field.FieldID,
      name: field.AliasID,
      description: field.Descr,
      type: field.FieldType,
      schemaType: this.mapSapTypeToSchemaType(field.FieldType),
      size: field.SizeID,
      required: field.NotNull === 'Y',
      displayText: `${field.AliasID} - ${field.Descr} (${field.FieldType})`
    };
  }

  /**
   * 批量格式化SAP字段
   */
  formatSapFieldsForDisplay(fields: SapField[]): Array<ReturnType<typeof this.formatSapFieldForDisplay>> {
    return fields.map(field => this.formatSapFieldForDisplay(field));
  }

  /**
   * 根据条件过滤SAP字段
   */
  filterSapFields(
    fields: SapField[], 
    filters: {
      searchText?: string;
      fieldType?: string;
      requiredOnly?: boolean;
    }
  ): SapField[] {
    return fields.filter(field => {
      // 搜索文本过滤
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        const matchesSearch = 
          field.AliasID.toLowerCase().includes(searchLower) ||
          field.Descr.toLowerCase().includes(searchLower) ||
          field.FieldType.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // 字段类型过滤
      if (filters.fieldType && field.FieldType !== filters.fieldType) {
        return false;
      }

      // 必填字段过滤
      if (filters.requiredOnly && field.NotNull !== 'Y') {
        return false;
      }

      return true;
    });
  }

  /**
   * 获取默认的SAP配置
   */
  getDefaultSapConfig(): SapConfig {
    return {
      company: 'SBO_DEMO_AI',
      tableId: 'ODRF',
      lang: 'en_US',
      fieldType: 'document'
    };
  }

  /**
   * 验证SAP配置的本地检查
   */
  validateSapConfigLocal(config: SapConfig): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!config.company || config.company.trim() === '') {
      errors.push('公司代码不能为空');
    }
    
    if (!config.tableId || config.tableId.trim() === '') {
      errors.push('表ID不能为空');
    }
    
    if (!config.lang || config.lang.trim() === '') {
      errors.push('语言代码不能为空');
    }
    
    // 验证语言代码格式
    const validLangs = ['en_US', 'zh_CN', 'zh_TW', 'ja_JP', 'ko_KR'];
    if (config.lang && !validLangs.includes(config.lang)) {
      errors.push(`不支持的语言代码: ${config.lang}，支持的语言: ${validLangs.join(', ')}`);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export const SAPB1Service = new SAPB1ServiceClass();
export default SAPB1Service;